/**
 * Create / Edit Expense Page (UI-1 v3 layout, BE-5a/b/c, BE-19 data)
 *
 * Routes: /accounting/expenses/new                 (create; ?refundOf=<id> starts a refund, ?copyOf=<id> copies one)
 *         /accounting/expenses/:id/edit            (edit a DRAFT or PENDING_APPROVAL expense)
 * "Recurring monthly" also creates a monthly template (not for refunds or cheques).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem, ListSubheader,
  InputAdornment, RadioGroup, FormControlLabel, Radio, Collapse, Checkbox, Alert, Skeleton, Snackbar, IconButton, Divider, CircularProgress,
} from '@mui/material';
import { Save, ArrowBack, ExpandMore, ExpandLess, UploadFile } from '@mui/icons-material';
import { fontSize, fontWeight } from '../../utils/accounting/styles';
import { formatCurrency, formatDate } from '../../utils/accounting/formatters';
import { expenseService, encodePayFrom, decodePayFrom } from '../../services/accounting/expense.service';
import ApiService from '../../services/api.service';
import { staffService } from '../../services/accounting/staff.service';
import profitShareService from '../../services/accounting/profitShare.service';
import { serverMessage } from '../../utils/apiConvert';

const today = () => new Date().toISOString().slice(0, 10);
const previousMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthLabel = (ym) => new Date(`${ym}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

// Compact menus to match the 36px fields.
const MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 340,
      '& .MuiList-root': { py: 0.5 },
      '& .MuiMenuItem-root': { fontSize: '0.8125rem', minHeight: 28, py: 0.25, pl: 2.5 },
    },
  },
};
const subheaderSx = {
  lineHeight: '24px', fontSize: '0.65rem', fontWeight: fontWeight.semibold, letterSpacing: '0.06em',
  textTransform: 'uppercase', color: 'text.secondary', pl: 1.5,
};
// Salary payees: employees, and members without a staff profile yet (the server refuses one who was never paid a salary).
const SALARY_TYPES = ['EMPLOYEE'];

const EMPTY_FORM = {
  date: today(),
  description: '',
  amount: '',
  vatAmount: '',
  categoryId: '',
  expenseType: 'PAID',
  payFrom: '',
  payeeName: '',
  payeeId: '',
  salaryMonth: previousMonth(),
  chequeNumber: '',
  chequeDate: '',
  originalExpenseId: '',
  reference: '',
  department: '',
  remarks: '',
  isRecurring: false,
  recurringDay: 1,
  vatRecoverable: false,
  isTaxDeductible: true,
};

const labelSx = { '& .MuiFormControlLabel-label': { fontSize: fontSize.sm } };

const Section = ({ title, hint, children, sx }) => (
  <Paper sx={{ p: 2.5, mb: 2, ...sx }}>
    {title && (
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: 'secondary.main' }}>{title}</Typography>
        {hint && <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mt: 0.25 }}>{hint}</Typography>}
      </Box>
    )}
    {children}
  </Paper>
);
const PreviewLine = ({ side, label, amount }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
    <Typography sx={{ fontSize: fontSize.sm }}>
      <Box component="span" sx={{ display: 'inline-block', width: 28, color: 'text.secondary', fontWeight: fontWeight.semibold }}>{side}</Box>
      {label}
    </Typography>
    <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, whiteSpace: 'nowrap' }}>{formatCurrency(amount)}</Typography>
  </Box>
);

const CreateExpense = ({ currentUser }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const refundOf = searchParams.get('refundOf');
  const copyOf = searchParams.get('copyOf');
  const isEdit = Boolean(id);
  const typingCenterId = currentUser?.typingCenterId;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [drawingPartnerId, setDrawingPartnerId] = useState('');
  const [businessType, setBusinessType] = useState('SOLE_ESTABLISHMENT');
  const [snackbar, setSnackbar] = useState(null);
  const [categories, setCategories] = useState([]);
  const [payFromOptions, setPayFromOptions] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [staffProfiles, setStaffProfiles] = useState([]);
  const [amountTyped, setAmountTyped] = useState(false);
  const [standingSalary, setStandingSalary] = useState(undefined); // undefined = not looked up, null = none
  const [refundable, setRefundable] = useState([]);
  const [showOptional, setShowOptional] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM, expenseType: refundOf ? 'RETURN' : 'PAID', originalExpenseId: refundOf || '' });
  const [errors, setErrors] = useState({});
  const [receiptFile, setReceiptFile] = useState(null);

  const isReturn = form.expenseType === 'RETURN';
  const selectedCategory = categories.find((c) => c.id === form.categoryId);
  const isSalary = !!selectedCategory?.isSalary;
  const { paymentAccountType } = decodePayFrom(form.payFrom);
  const isCheque = paymentAccountType === 'CHEQUE';
  const original = refundable.find((x) => x.id === form.originalExpenseId);
  const canRecur = !isEdit && !isReturn;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cats, options, team, profiles, approved] = await Promise.all([
          expenseService.getCategories(),
          expenseService.getPayFromOptions(),
          expenseService.getTeamMembers(typingCenterId).catch(() => []),
          staffService.list().catch(() => []),
          expenseService.list({ status: 'APPROVED', size: 200 }).then((p) => p.content).catch(() => []),
        ]);
        if (!alive) return;
        setCategories(cats.filter((c) => c.active !== false));
        setPayFromOptions(options);
        setTeamMembers(team);
        setStaffProfiles(profiles || []);
        setRefundable(approved.filter((x) => x.expenseType !== 'RETURN' && !x.sourceType?.endsWith('FINE')));

        ApiService.get('/api/settings/accounting')
          .then((r) => { if (alive) setBusinessType(r?.data?.data?.business_type === 'COMPANY' ? 'COMPANY' : 'SOLE_ESTABLISHMENT'); })
          .catch(() => {});
        const sourceId = id || copyOf;
        const salaryCategories = cats.filter((c) => c.active !== false && c.isSalary);
        if (!sourceId && !refundOf && searchParams.get('payeeId')) {
          setForm((prev) => ({
            ...prev,
            categoryId: salaryCategories.length === 1 ? salaryCategories[0].id : prev.categoryId,
            payeeId: searchParams.get('payeeId'),
            salaryMonth: searchParams.get('salaryMonth') || prev.salaryMonth,
          }));
        }
        if (sourceId) {
          const e = await expenseService.getById(sourceId);
          if (!alive) return;
          setForm({
            ...EMPTY_FORM,
            date: isEdit ? (e.date || '') : today(),
            description: e.description || '',
            amount: e.amount ?? '',
            vatAmount: e.vatAmount ?? '',
            categoryId: e.category?.id || '',
            expenseType: e.expenseType || 'PAID',
            payFrom: encodePayFrom(e.paymentAccount?.type, e.paymentAccount?.id),
            payeeName: e.payeeName || '',
            payeeId: e.payeeId || '',
            salaryMonth: e.salaryMonth ? String(e.salaryMonth).slice(0, 7) : previousMonth(),
            chequeNumber: isEdit ? (e.chequeNumber || '') : '',
            chequeDate: isEdit ? (e.chequeDate || '') : '',
            originalExpenseId: e.originalExpenseId || '',
            reference: e.reference || '',
            department: e.department || '',
            remarks: e.remarks || '',
            vatRecoverable: !!e.vatRecoverable,
            isTaxDeductible: e.isTaxDeductible !== false,
          });
          if (e.amount != null) setAmountTyped(true);
          if (e.reference || e.department || e.remarks || e.vatRecoverable || e.isTaxDeductible === false) setShowOptional(true);
        }
      } catch (err) {
        if (alive) setError(serverMessage(err, 'Failed to load the expense form'));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, copyOf, isEdit, typingCenterId, refundOf, searchParams]);

  useEffect(() => {
    if (!isReturn || !original || isEdit) return;
    setForm((prev) => ({ ...prev, description: prev.description || `Refund — ${original.description}` }));
  }, [isReturn, original, isEdit]);

  // Salary pre-fill: the standing salary for the chosen month, only while the amount has not been typed.
  useEffect(() => {
    if (!isSalary || !form.payeeId || !form.salaryMonth) { setStandingSalary(undefined); return undefined; }
    let alive = true;
    staffService.salaryFor(form.payeeId, form.salaryMonth)
      .then((r) => {
        if (!alive) return;
        const total = r?.total ?? null;
        setStandingSalary(total);
        if (total !== null && !amountTyped) setForm((prev) => ({ ...prev, amount: Number(total).toFixed(2) }));
      })
      .catch(() => alive && setStandingSalary(null));
    return () => { alive = false; };
  }, [isSalary, form.payeeId, form.salaryMonth, amountTyped]);

  const set = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      const salaryCategory = categories.find((c) => c.id === next.categoryId)?.isSalary;
      if (field === 'amount' && !isReturn && !salaryCategory) {
        const amt = parseFloat(value) || 0;
        next.vatAmount = amt ? (amt * 0.05).toFixed(2) : '';
      }
      if (field === 'categoryId') {
        if (!salaryCategory) next.payeeId = '';
        if (salaryCategory) { next.payeeName = ''; next.vatAmount = ''; next.vatRecoverable = false; }
        if (salaryCategory && !amountTyped) next.amount = '';
      }
      if (field === 'payFrom' && decodePayFrom(value).paymentAccountType !== 'CHEQUE') {
        next.chequeNumber = '';
        next.chequeDate = '';
      }
      if (field === 'payFrom' && decodePayFrom(value).paymentAccountType === 'CHEQUE') next.isRecurring = false;
      if (field === 'expenseType') {
        if (value !== 'PAID') { next.payFrom = ''; next.chequeNumber = ''; next.chequeDate = ''; }
        if (value !== 'RETURN') next.originalExpenseId = '';
        if (value === 'RETURN') next.isRecurring = false;
      }
      if (field === 'vatAmount' && !(parseFloat(value) > 0)) next.vatRecoverable = false;
      return next;
    });
    setErrors((prev) => ({ ...prev, [field]: null }));
  };
  const handleChange = (field) => (e) => {
    if (field === 'amount') setAmountTyped(e.target.value !== '');
    set(field, e.target.type === 'checkbox' ? e.target.checked : e.target.value);
  };

  const twoDecimals = (v) => /^\d+(\.\d{1,2})?$/.test(String(v));

  const validate = () => {
    const e = {};
    if (!form.date) e.date = 'Required';
    else if (form.date > today()) e.date = 'Cannot be in the future';
    if (!form.description.trim()) e.description = 'Required';
    if (!form.amount || parseFloat(form.amount) <= 0 || !twoDecimals(form.amount)) e.amount = 'Enter a valid amount';
    if (form.vatAmount !== '' && !twoDecimals(form.vatAmount)) e.vatAmount = 'At most 2 decimals';
    if (isReturn) {
      if (!form.originalExpenseId) e.originalExpenseId = 'Choose the expense this refund is for';
    } else {
      if (!form.categoryId) e.categoryId = 'Required';
      if (isSalary && !form.payeeId) e.payeeId = 'Choose the team member';
      if (isSalary && !form.salaryMonth) e.salaryMonth = 'Required';
      else if (isSalary && form.date && form.salaryMonth > form.date.slice(0, 7)) e.salaryMonth = 'A salary cannot be paid before its month';
      if (form.expenseType === 'PAID' && !form.payFrom) e.payFrom = 'Select payment account';
      if (isCheque && !form.chequeNumber.trim()) e.chequeNumber = 'Required';
      if (isCheque && !form.chequeDate) e.chequeDate = 'Required';
      if (form.isRecurring && !(Number(form.recurringDay) >= 1 && Number(form.recurringDay) <= 28)) e.recurringDay = '1–28';
    }
    setErrors(e);
    if (Object.keys(e).some((k) => ['recurringDay', 'vatAmount'].includes(k))) setShowOptional(true);
    return Object.keys(e).length === 0;
  };

  const toRequest = () => {
    const common = {
      date: form.date,
      description: form.description.trim(),
      amount: form.amount,
      vatAmount: form.vatAmount === '' ? 0 : form.vatAmount,
      expenseType: form.expenseType,
      reference: form.reference,
      department: form.department,
      remarks: form.remarks,
    };
    if (isReturn) return { ...common, originalExpenseId: form.originalExpenseId };
    return {
      ...common,
      categoryId: form.categoryId,
      payeeName: isSalary ? undefined : form.payeeName,
      payeeId: isSalary ? form.payeeId : undefined,
      salaryMonth: isSalary ? `${form.salaryMonth}-01` : undefined,
      vatRecoverable: form.vatRecoverable,
      isTaxDeductible: form.isTaxDeductible,
      ...(form.expenseType === 'PAID' ? decodePayFrom(form.payFrom) : {}),
      ...(isCheque ? { chequeNumber: form.chequeNumber.trim(), chequeDate: form.chequeDate } : {}),
    };
  };

  const toTemplate = () => ({
    categoryId: form.categoryId,
    description: form.description.trim(),
    payeeName: isSalary ? undefined : form.payeeName,
    payeeId: isSalary ? form.payeeId : undefined,
    amount: form.amount,
    vatAmount: form.vatAmount === '' ? 0 : form.vatAmount,
    vatRecoverable: form.vatRecoverable,
    expenseType: form.expenseType,
    isTaxDeductible: form.isTaxDeductible,
    dayOfMonth: Number(form.recurringDay),
    active: true,
    ...(form.expenseType === 'PAID' ? decodePayFrom(form.payFrom) : {}),
  });

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setError(null);
    try {
      const saved = isEdit ? await expenseService.update(id, toRequest()) : await expenseService.create(toRequest());
      const savedId = saved?.id || id;
      const problems = [];
      if (canRecur && form.isRecurring) {
        try { await expenseService.createTemplate(toTemplate()); } catch (err) { problems.push(`the monthly template was not created (${serverMessage(err, 'failed')})`); }
      }
      if (receiptFile && savedId) {
        try { await expenseService.attachReceipt(savedId, receiptFile); } catch (err) { problems.push(`the receipt was not uploaded (${serverMessage(err, 'failed')})`); }
      }
      if (problems.length) {
        setError(`Expense saved, but ${problems.join(' and ')}.`);
        setTimeout(() => navigate(`/accounting/expenses/${savedId}`), 2500);
        return;
      }
      setSnackbar(isEdit ? 'Expense updated' : form.isRecurring ? 'Expense saved — it will repeat monthly' : 'Expense submitted for approval');
      setTimeout(() => navigate(`/accounting/expenses/${savedId}`), 600);
    } catch (err) {
      const message = serverMessage(err, 'Failed to save expense');
      setError(message);
      setDrawingPartnerId('');
      if (/sole establishment/i.test(message) && form.payeeId) {
        profitShareService.getPartners()
          .then((r) => setDrawingPartnerId((r?.data || []).find((p) => p.userId === form.payeeId)?.id || ''))
          .catch(() => {});
      }
    } finally {
      setSaving(false);
    }
  };

  const groupedCategories = useMemo(() => {
    const byGroup = {};
    categories.forEach((c) => { const g = c.group?.name || 'Other'; (byGroup[g] = byGroup[g] || []).push(c); });
    return Object.entries(byGroup);
  }, [categories]);

  const salaryPayees = useMemo(() => {
    const profileByUser = Object.fromEntries(staffProfiles.map((p) => [p.userId, p]));
    return teamMembers.filter((m) => {
      if (m.id === form.payeeId) return true; // keep the current payee when editing
      const p = profileByUser[m.id];
      return p ? p.active !== false && SALARY_TYPES.includes(p.employmentType) : true;
    });
  }, [teamMembers, staffProfiles, form.payeeId]);

  const groupedPayFrom = useMemo(() => {
    const byGroup = {};
    payFromOptions
      .filter((o) => !(isReturn && o.group === 'Cheque'))
      .forEach((o) => { (byGroup[o.group] = byGroup[o.group] || []).push(o); });
    return Object.entries(byGroup);
  }, [payFromOptions, isReturn]);

  const amt = parseFloat(form.amount) || 0;
  const vat = parseFloat(form.vatAmount) || 0;
  const total = amt + vat;
  const payFromOption = payFromOptions.find((o) => o.value === form.payFrom);
  const payFromLabel = payFromOption?.label;
  const goesNegative = form.expenseType === 'PAID' && ['Bank', 'Cash'].includes(payFromOption?.group)
    && payFromOption?.balance != null && total > Number(payFromOption.balance);
  const crLabel = form.expenseType === 'PAID'
    ? (isCheque ? 'Cheques Issued' : (payFromLabel || 'Bank/Cash'))
    : 'Accounts Payable';
  // When the chosen month is before the person's salary starts, say when it starts and offer that month.
  const payeeSalary = staffProfiles.find((p) => p.userId === form.payeeId)?.currentSalary;
  const startsLater = payeeSalary && String(payeeSalary.effectiveFrom).slice(0, 7) > form.salaryMonth
    ? String(payeeSalary.effectiveFrom).slice(0, 7) : null;
  const linkSx = { cursor: 'pointer', color: 'secondary.main', fontWeight: fontWeight.semibold, ml: 0.5 };
  let salaryHint = null;
  const lateHint = isSalary && !isReturn && form.expenseType === 'PAID' && form.date && form.salaryMonth && form.salaryMonth < form.date.slice(0, 7)
    ? `Paid now counts as a cost in the month of the date. To keep it in ${monthLabel(form.salaryMonth)}, choose Credit (Pay Later) dated that month's end and mark it paid.`
    : null;
  if (isSalary && !isReturn && form.payeeId && standingSalary !== undefined) {
    if (standingSalary !== null) {
      salaryHint = `Salary for ${monthLabel(form.salaryMonth)}: ${formatCurrency(standingSalary)}. Edit if different.`;
    } else if (startsLater) {
      salaryHint = (
        <>
          Salary starts {monthLabel(startsLater)} ({formatCurrency(payeeSalary.total)}).
          <Box component="span" sx={linkSx} onClick={() => set('salaryMonth', startsLater)}>Use {monthLabel(startsLater)}</Box>
        </>
      );
    } else {
      salaryHint = `No salary set for ${monthLabel(form.salaryMonth)}. Set it on Team.`;
    }
  }
  const back = () => navigate(isEdit ? `/accounting/expenses/${id}` : '/accounting/expenses');
  const errorText = (key) => errors[key] && (
    <Typography sx={{ fontSize: fontSize.xs, color: 'error.main', mt: 0.5, ml: 1.5 }}>{errors[key]}</Typography>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      width: 0, minWidth: '100%',
      '& .MuiInputBase-root': { height: 36 },
      '& .MuiInputBase-multiline': { height: 'auto' },
      '& .MuiInputBase-input': { fontSize: '0.8125rem' },
      '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
      '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 9px) scale(1)' },
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <IconButton size="small" onClick={back}><ArrowBack fontSize="small" /></IconButton>
        <Box>
          <Typography sx={{ fontSize: '1rem', fontWeight: fontWeight.semibold }}>
            {isEdit ? 'Edit Expense' : isReturn ? 'Record Refund' : 'New Expense'}
          </Typography>
          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
            {isReturn ? 'Money given back on an earlier expense.' : 'Saved expenses go for approval before they post to the books.'}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Form */}
        <Grid item xs={12} lg={8}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}
            action={drawingPartnerId ? (
              <Button color="inherit" size="small" onClick={() => navigate(`/accounting/drawings?partnerId=${drawingPartnerId}`)}>Record a drawing</Button>
            ) : null}>
            {error}
          </Alert>
        )}
        {copyOf && !isEdit && (
          <Alert severity="info" sx={{ mb: 2 }}>Copied from a rejected expense — check the details and save it as a new expense.</Alert>
        )}

        {/* Essential Fields */}
        <Section title="Essential Fields" hint="What was bought, from whom, and how much.">
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={4}>
              <TextField label="Date" type="date" value={form.date} onChange={handleChange('date')} error={!!errors.date}
                helperText={errors.date} fullWidth InputLabelProps={{ shrink: true }} inputProps={{ max: today() }} />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField label="Description" value={form.description} onChange={handleChange('description')} error={!!errors.description}
                helperText={errors.description} fullWidth placeholder="What was this expense for?" />
            </Grid>

            {isReturn ? (
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.originalExpenseId}>
                  <InputLabel>Refund of</InputLabel>
                  <Select value={form.originalExpenseId} label="Refund of" onChange={handleChange('originalExpenseId')} disabled={isEdit} MenuProps={MENU_PROPS}>
                    {refundable.map((x) => (
                      <MenuItem key={x.id} value={x.id}>
                        {x.expenseNumber} - {x.description} · {formatCurrency(x.total)} · {formatDate(x.date)}
                      </MenuItem>
                    ))}
                  </Select>
                  {errorText('originalExpenseId')}
                </FormControl>
                {original && (
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mt: 1 }}>
                    Category, payee, VAT treatment and Pay From are taken from {original.expenseNumber}
                    {original.category?.name ? ` (${original.category.name})` : ''}. Refunds cannot exceed its total.
                  </Typography>
                )}
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.categoryId}>
                    <InputLabel>Category</InputLabel>
                    <Select value={form.categoryId} label="Category" onChange={handleChange('categoryId')} MenuProps={MENU_PROPS}>
                      {groupedCategories.flatMap(([group, cats]) => [
                        <ListSubheader key={`g-${group}`} sx={subheaderSx}>{group}</ListSubheader>,
                        ...cats.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>),
                      ])}
                    </Select>
                    {errorText('categoryId')}
                    {isSalary && !isReturn && businessType === 'SOLE_ESTABLISHMENT' && /management/i.test(selectedCategory?.name || '') && (
                      <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mt: 0.5, ml: 1.5 }}>
                        For LLC managers — a sole owner's pay is recorded as a drawing.
                      </Typography>
                    )}
                    {categories.length === 0 && (
                      <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mt: 0.5, ml: 1.5 }}>
                        No categories yet — seed the defaults in Settings → Expense Categories.
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {isSalary ? (
                    <FormControl fullWidth error={!!errors.payeeId}>
                      <InputLabel>Employee</InputLabel>
                      <Select value={form.payeeId} label="Employee" onChange={handleChange('payeeId')} MenuProps={MENU_PROPS}>
                        {salaryPayees.map((m) => (
                          <MenuItem key={m.id} value={m.id}>
                            {m.fullName || m.username}
                            <Typography component="span" sx={{ fontSize: fontSize.xs, color: 'text.secondary', ml: 1 }}>({m.roleName || m.role || 'Staff'})</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                      {errorText('payeeId')}
                    </FormControl>
                  ) : (
                    <TextField label="Vendor / Payee" value={form.payeeName} onChange={handleChange('payeeName')}
                      fullWidth placeholder="e.g. AWS, DEWA, Landlord" />
                  )}
                </Grid>
              </>
            )}

            {isSalary && !isReturn && (
              <Grid item xs={12} sm={6}>
                <TextField label="Salary for month" type="month" value={form.salaryMonth} onChange={handleChange('salaryMonth')}
                  error={!!errors.salaryMonth} helperText={errors.salaryMonth || lateHint || ' '} fullWidth InputLabelProps={{ shrink: true }} />
              </Grid>
            )}
            {isSalary && !isReturn && <Grid item xs={false} sm={6} />}
            <Grid item xs={12} sm={6}>
              <TextField label="Amount" type="number" value={form.amount} onChange={handleChange('amount')} error={!!errors.amount}
                helperText={errors.amount || salaryHint || ' '} fullWidth
                InputProps={{ endAdornment: <InputAdornment position="end">AED</InputAdornment>, inputProps: { min: 0, step: '0.01' } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="VAT" type="number" value={form.vatAmount} onChange={handleChange('vatAmount')} error={!!errors.vatAmount}
                helperText={errors.vatAmount || (isReturn || isSalary ? ' ' : 'Auto 5% of the amount')} fullWidth
                InputProps={{ endAdornment: <InputAdornment position="end">AED</InputAdornment>, inputProps: { min: 0, step: '0.01' } }} />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: 'text.secondary', mb: 0.5 }}>Type</Typography>
              <RadioGroup row value={form.expenseType} onChange={(e) => set('expenseType', e.target.value)} sx={{ gap: { xs: 0, sm: 3 } }}>
                <FormControlLabel value="PAID" control={<Radio size="small" />} label="Direct Payment" sx={labelSx} disabled={isEdit} />
                <FormControlLabel value="PAYABLE" control={<Radio size="small" />} label="Credit (Pay Later)" sx={labelSx} disabled={isEdit} />
                <FormControlLabel value="RETURN" control={<Radio size="small" />} label="Refund Received" sx={labelSx} disabled={isEdit} />
              </RadioGroup>
            </Grid>

            {form.expenseType === 'PAID' && (
              <>
                <Grid item xs={12} sm={isCheque ? 6 : 8}>
                  <FormControl fullWidth error={!!errors.payFrom}>
                    <InputLabel>Pay From</InputLabel>
                    <Select value={form.payFrom} label="Pay From" onChange={handleChange('payFrom')} MenuProps={MENU_PROPS}>
                      {groupedPayFrom.flatMap(([group, opts]) => [
                        <ListSubheader key={`h-${group}`} sx={subheaderSx}>{group}</ListSubheader>,
                        ...opts.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>),
                      ])}
                    </Select>
                    {errorText('payFrom')}
                    {goesNegative && (
                      <Typography sx={{ fontSize: fontSize.xs, color: 'warning.main', mt: 0.5, ml: 1.5 }}>
                        This will make {payFromLabel} negative (balance {formatCurrency(payFromOption.balance)})
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                {isCheque && (
                  <>
                    <Grid item xs={6} sm={3}>
                      <TextField label="Cheque #" value={form.chequeNumber} onChange={handleChange('chequeNumber')} error={!!errors.chequeNumber}
                        helperText={errors.chequeNumber} fullWidth placeholder="CHQ-001" />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField label="Cheque Date" type="date" value={form.chequeDate} onChange={handleChange('chequeDate')} error={!!errors.chequeDate}
                        helperText={errors.chequeDate || 'May be in the future'} fullWidth InputLabelProps={{ shrink: true }} />
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
        </Section>

        {/* Optional Fields */}
        <Paper sx={{ mb: 2, overflow: 'hidden' }}>
          <Box onClick={() => setShowOptional(!showOptional)}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', px: 2.5, py: 1.5, '&:hover': { bgcolor: 'action.hover' } }}>
            <Box>
              <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: 'secondary.main' }}>More options</Typography>
              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Reference, receipt, recurring and tax settings</Typography>
            </Box>
            <IconButton size="small">{showOptional ? <ExpandLess /> : <ExpandMore />}</IconButton>
          </Box>
          <Collapse in={showOptional}>
            <Box sx={{ px: 2.5, pb: 2.5, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField label="Reference" value={form.reference} onChange={handleChange('reference')} fullWidth placeholder="Invoice or bill number" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Department" value={form.department} onChange={handleChange('department')} fullWidth />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Remarks" value={form.remarks} onChange={handleChange('remarks')} fullWidth multiline rows={2} />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Button component="label" variant="contained" size="small" startIcon={<UploadFile />} sx={{ textTransform: 'none' }}>
                        {receiptFile ? 'Change receipt' : 'Upload Receipt'}
                        <input type="file" hidden accept="image/*,application/pdf" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} />
                      </Button>
                      {receiptFile && <Button size="small" color="inherit" onClick={() => setReceiptFile(null)} sx={{ textTransform: 'none' }}>Remove</Button>}
                    </Box>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', wordBreak: 'break-all' }}>
                      {receiptFile ? receiptFile.name : 'Image or PDF, up to 10 MB'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {canRecur && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <FormControlLabel
                          control={<Checkbox size="small" checked={form.isRecurring} onChange={handleChange('isRecurring')} disabled={isCheque} />}
                          label={<Typography sx={{ fontSize: fontSize.sm }}>Recurring monthly{isCheque ? ' (not for cheques)' : ''}</Typography>} />
                        {form.isRecurring && (
                          <TextField label="Day of month" type="number" value={form.recurringDay} onChange={handleChange('recurringDay')} size="small"
                            sx={{ width: 130 }} inputProps={{ min: 1, max: 28 }} error={!!errors.recurringDay} helperText={errors.recurringDay} />
                        )}
                      </Box>
                    )}
                    {canRecur && form.isRecurring && (
                      <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', ml: 4, mb: 0.5 }}>
                        A draft is prepared on day {form.recurringDay || '…'} of each month for you to review.
                      </Typography>
                    )}
                    {!isReturn && (
                      <FormControlLabel
                        control={<Checkbox size="small" checked={form.vatRecoverable} onChange={handleChange('vatRecoverable')} disabled={!(vat > 0)} />}
                        label={<Typography sx={{ fontSize: fontSize.sm }}>VAT recoverable</Typography>} />
                    )}
                    {!isReturn && (
                      <FormControlLabel
                        control={<Checkbox size="small" checked={form.isTaxDeductible} onChange={handleChange('isTaxDeductible')} />}
                        label={<Typography sx={{ fontSize: fontSize.sm }}>Tax deductible</Typography>} />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>

        </Grid>

        {/* Right Column - Summary */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 2, position: 'sticky', top: 20 }}>
            <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: 'secondary.main', mb: 1.5 }}>
              Summary
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Amount</Typography>
              <Typography sx={{ fontSize: fontSize.sm }}>{formatCurrency(amt)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>VAT</Typography>
              <Typography sx={{ fontSize: fontSize.sm }}>{formatCurrency(vat)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold }}>Total</Typography>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: fontWeight.bold }}>{formatCurrency(total)}</Typography>
            </Box>

            {amt > 0 && (
              <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                <Typography sx={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, mb: 0.25 }}>Posting Preview</Typography>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 1 }}>Posted when the expense is approved.</Typography>
                {isReturn ? (
                  <Typography sx={{ fontSize: fontSize.sm }}>
                    Reverses {formatCurrency(total)} of {original?.expenseNumber || 'the original'} back to where it was paid from.
                  </Typography>
                ) : (
                  <>
                    <PreviewLine side="Dr" label={selectedCategory?.name || 'Expense'} amount={amt} />
                    {vat > 0 && <PreviewLine side="Dr" label={form.vatRecoverable ? 'Input VAT' : 'Expense (VAT)'} amount={vat} />}
                    <Divider sx={{ my: 0.75 }} />
                    <PreviewLine side="Cr" label={crLabel} amount={total} />
                  </>
                )}
              </Box>
            )}

            <Button fullWidth variant="contained" size="small" onClick={handleSave} disabled={saving} sx={{ mt: 2, textTransform: 'none' }}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 16 }} />}>
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Save Expense'}
            </Button>
            <Button fullWidth size="small" color="inherit" onClick={back} sx={{ mt: 1, textTransform: 'none', color: 'text.secondary' }}>
              Cancel
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar(null)} message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default CreateExpense;
